import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Inject, Injectable, LOCALE_ID, NgModule } from '@angular/core';

import { AppComponent } from './app.component';

import { AppRouteModule } from './app.route';

import { environment } from '../environments/environment';
import {
  LeafConfig,
  LeafAdminModule,
  LeafConfigServiceModule,
  LeafSessionModule,
  LeafSessionService,
  LeafUploadFileModule,
  LeafWebImagesSeekerModule,
  leafCoreStore,
  LeafWebSocketModule,
  LeafApiClientConfig,
  LeafApiClientConfigServiceModule,
  LeafPasswordForgottenVanillaModule,
  LeafNavigationModule,
  SessionEffects,
  AdaptiveViewModule,
  StatisticsEffects,
  GenericFormModule,
  LeafHeaderAccountModule,
  AdminSettingsPageModule,
  messengerReducer,
  MessengerEffects,
  EmailingEffects,
  EligibilitiesEffects,
  EligibilitiesApiClientModule,
} from '@input-output-labs/ngleaf';

import { MatDividerModule } from '@angular/material/divider';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { CommonModule, getLocaleFirstDayOfWeek } from '@angular/common';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { EffectsModule } from '@ngrx/effects';
import { DateAdapter, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { Platform } from '@angular/cdk/platform';
import { MatDatepickerModule } from '@angular/material/datepicker';

const leafConfig: LeafConfig = {
  serverUrl: environment.serverUrl,
  serverWSBrokerUrl: '/ws',
  navigation: {
    authGuardErrorRedirect: '/welcome/login',
    adminGuardErrorRedirect: '/forbidden',
    loginSuccessRedirect: '',
    logoutRedirect: '/welcome/login',
    registerSuccessRedirect: ''
  },
  apis: {
    pixabay_api_key: environment.API_KEY_PIXABAY
  },
  setupConfig: {
    notifications: true,
    organizations: true,
    eligibilities: true,
  }
};

const leafApiClientConfig: LeafApiClientConfig = {
  serverUrl: environment.serverUrl
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

@Injectable()
export class LocaleDateAdapter extends NativeDateAdapter {
  constructor(@Inject(LOCALE_ID) public locale: string) {
    super(locale, new Platform(0));
  }

  getFirstDayOfWeek() {
    return getLocaleFirstDayOfWeek(this.locale);
  }
}

@NgModule({ declarations: [AppComponent],
    bootstrap: [AppComponent], imports: [CommonModule,
        BrowserModule,
        BrowserAnimationsModule,
        AppRouteModule,
        /* Translation module */
        TranslateModule.forRoot({
            defaultLanguage: 'fr',
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        }),
        /* Material design library import */
        MatDividerModule,
        MatNativeDateModule,
        MatDatepickerModule,
        /* Leaf library import */
        // Stores
        LeafConfigServiceModule.forRoot(leafConfig),
        LeafApiClientConfigServiceModule.forRoot(leafApiClientConfig),
        EffectsModule.forRoot([SessionEffects, StatisticsEffects, MessengerEffects, EmailingEffects, EligibilitiesEffects]),
        StoreModule.forRoot({
            ...leafCoreStore,
            messenger: messengerReducer
        }),
        StoreDevtoolsModule.instrument({
            maxAge: 25, // Retains last 25 states
            logOnly: environment.production, // Restrict extension to log-only mode
        }),
        // Services
        LeafAdminModule,
        LeafSessionModule,
        LeafWebImagesSeekerModule,
        LeafUploadFileModule,
        LeafWebSocketModule,
        // Directive
        AdaptiveViewModule,
        // Components
        LeafPasswordForgottenVanillaModule,
        GenericFormModule,
        LeafNavigationModule,
        LeafHeaderAccountModule,
        AdminSettingsPageModule,
        EligibilitiesApiClientModule], providers: [{
            provide: DateAdapter,
            useClass: LocaleDateAdapter
        }, provideHttpClient(withInterceptorsFromDi())] })
export class AppModule {
  constructor(leafSession: LeafSessionService, translate: TranslateService) {
    translate.setDefaultLang('fr');
    translate.use('fr');

    leafSession.init();
  }
}
